import React, { useState } from "react";
import Modal from "react-modal";
import "./css/CreateVoteModal.css";

const CreateVoteModal = ({ isOpen, onRequestClose, onCreateVote, createdVoteLink }) => {
    const [voteName, setVoteName] = useState("");
    const [voteType, setVoteType] = useState("time_selection");
    const [pickLimit, setPickLimit] = useState("");
    const [customOptions, setCustomOptions] = useState(["", ""]);
    const [timeSelection, setTimeSelection] = useState(
        Array(7).fill(Array(24).fill(false)) // 7 days, 24 hours
    );

    // Reset state to defaults
    const resetState = () => {
        setVoteName("");
        setVoteType("time_selection");
        setPickLimit("");
        setCustomOptions(["", ""]);
        setTimeSelection(Array(7).fill(Array(24).fill(false)));
    };

    const handleAddOption = () => {
        setCustomOptions((prevOptions) => [...prevOptions, ""]);
    };

    const handleOptionChange = (index, value) => {
        const updatedOptions = [...customOptions];
        updatedOptions[index] = value;
        setCustomOptions(updatedOptions);
    };

    const handleRemoveOption = (index) => {
        setCustomOptions((prevOptions) => prevOptions.filter((_, i) => i !== index));
    };

    const handleDaySelection = (dayIndex) => {
        const isAllSelected = timeSelection[dayIndex].every((hour) => hour);
        const updatedDay = Array(24).fill(!isAllSelected);
        const updatedSelection = [...timeSelection];
        updatedSelection[dayIndex] = updatedDay;
        setTimeSelection(updatedSelection);
    };

    const handleTimeSelectionChange = (dayIndex, hourIndex) => {
        const updatedDay = [...timeSelection[dayIndex]];
        updatedDay[hourIndex] = !updatedDay[hourIndex];
        const updatedSelection = [...timeSelection];
        updatedSelection[dayIndex] = updatedDay;
        setTimeSelection(updatedSelection);
    };
    const handleCreate = () => {
        if (!voteName) {
            alert("Vote Name is required");
            return;
        }

        const payload = {
            name: voteName,
            type: voteType,
        };

        if (voteType === "custom_selection") {
            if (!pickLimit || pickLimit <= 0) {
                alert("Pick Limit must be greater than 0");
                return;
            }
            if (customOptions.some((option) => option.trim() === "")) {
                alert("All custom options must be filled out");
                return;
            }
            payload.pickLimit = parseInt(pickLimit, 10);
            payload.options = customOptions;
        }

        if (voteType === "time_selection") {
            payload.restrictedTimeSlots = timeSelection
                .map((day, dayIndex) =>
                    day
                        .map((isSelected, hourIndex) =>
                            isSelected ? `${dayIndex}-${hourIndex}` : null
                        )
                        .filter(Boolean)
                )
                .flat();
        }

        console.log("Payload to be sent:", payload); // Debugging assistance
        onCreateVote(payload);
        resetState(); // Reset state after successful creation
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(createdVoteLink);
        alert("Vote link copied to clipboard!");
    };

    const handlePickLimitChange = (e) => {
        const value = e.target.value;
        if (value === "" || /^[0-9]+$/.test(value)) {
            setPickLimit(value);
        }
    };

    const handleClose = () => {
        resetState(); // Reset state when closing modal
        onRequestClose();
    };

    const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Create Vote"
            className="create-vote-modal"
            overlayClassName="create-vote-modal-overlay"
        >
            {!createdVoteLink ? (
                <>
                    <h2 className="modal-title">Create Vote</h2>
                    <div className="form-group">
                        <label htmlFor="voteName">Vote Name:</label>
                        <input
                            id="voteName"
                            type="text"
                            value={voteName}
                            onChange={(e) => setVoteName(e.target.value)}
                            placeholder="Enter vote name"
                        />
                    </div>
                    <div className="form-group">
                        <label>Vote Type:</label>
                        <div className="radio-group">
                            <label>
                                <input
                                    type="radio"
                                    name="voteType"
                                    value="time_selection"
                                    checked={voteType === "time_selection"}
                                    onChange={(e) => setVoteType(e.target.value)}
                                />
                                Time Selection
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="voteType"
                                    value="custom_selection"
                                    checked={voteType === "custom_selection"}
                                    onChange={(e) => setVoteType(e.target.value)}
                                />
                                Custom Selection
                            </label>
                        </div>
                    </div>
                    {voteType === "time_selection" && (
                        <div className="time-selection">
                            <p>
                                The time selection vote will allow players to select any of the selected timeslots chosen here.
                                select the check at the top to allow that entire day
                                or
                                select specific timeslots within each day
                            </p>
                            <table>
                                <thead>
                                    <tr>
                                        <th></th>
                                        {daysOfWeek.map((day, dayIndex) => (
                                            <th key={day}>
                                                <label>{day}</label>
                                                <input
                                                    type="checkbox"
                                                    onChange={() => handleDaySelection(dayIndex)}
                                                    checked={timeSelection[dayIndex].every((hour) => hour)}
                                                />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...Array(24).keys()].map((hour) => (
                                        <tr key={hour}>
                                            <td>{`${hour.toString().padStart(2, "0")}:00`}</td>
                                            {daysOfWeek.map((_, dayIndex) => (
                                                <td key={`${dayIndex}-${hour}`}>
                                                    <input
                                                        type="checkbox"
                                                        onChange={() => handleTimeSelectionChange(dayIndex, hour)}
                                                        checked={timeSelection[dayIndex][hour]}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {voteType === "custom_selection" && (
                        <>
                            <div className="form-group">
                                <label htmlFor="pickLimit">Pick Limit:</label>
                                <input
                                    id="pickLimit"
                                    type="text"
                                    value={pickLimit}
                                    onChange={handlePickLimitChange}
                                    placeholder="Enter pick limit"
                                />
                            </div>
                            <div className="form-group">
                                <label>Custom Options:</label>
                                {customOptions.map((option, index) => (
                                    <div key={index} className="custom-option">
                                        <input
                                            type="text"
                                            value={option}
                                            onChange={(e) => handleOptionChange(index, e.target.value)}
                                            placeholder={`Option ${index + 1}`}
                                            className="custom-option-input"
                                        />
                                        {index >= 2 && (
                                            <button
                                                type="button"
                                                className="remove-option-button"
                                                onClick={() => handleRemoveOption(index)}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="add-option-button"
                                    onClick={handleAddOption}
                                >
                                    Add Another Option
                                </button>
                            </div>
                        </>
                    )}
                    <div className="modal-actions">
                        <button className="create-button" onClick={handleCreate}>
                            Create
                        </button>
                        <button className="cancel-button" onClick={handleClose}>
                            Cancel
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <h2 className="modal-title">Vote Created</h2>
                    <div className="vote-details">
                        <p>Your vote has been created successfully! Share this link:</p>
                        <p className="static-link">{createdVoteLink}</p>
                        <button onClick={handleCopyLink} className="copy-link-button">
                            Copy Link
                        </button>
                    </div>
                    <div className="modal-actions">
                        <button className="close-button" onClick={handleClose}>
                            Close
                        </button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default CreateVoteModal;
